import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RetireDialog } from "@/components/RecordEditor";
import { ModuleNav } from "./ModuleNav";
import { Plus, Search, Download, Ban, Undo2, AlertTriangle, Users } from "lucide-react";

const EMPTY = { case_id: "", consultant_id: "", reason: "Difficult diagnosis", mandated: false, question: "" };

const AGREE_STYLE = {
  Agree: "bg-[#134e4a] text-white",
  "Minor disagreement": "bg-amber-100 text-amber-900",
  "Major disagreement": "bg-red-800 text-white",
  "Deferred to outside consult": "bg-sky-100 text-sky-900",
  Pending: "bg-slate-200 text-slate-700",
};

const Stat = ({ label, value, sub, tone = "" }) => (
  <div className="border border-slate-200 p-3" data-testid={`consult-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-0.5 text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function Consults() {
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState([]);
  const [filters, setFilters] = useState({ q: "", flag: "", voided: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [detail, setDetail] = useState(null);
  const [voidRow, setVoidRow] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const [list, sum] = await Promise.all([
        api.get("/consults", { params: p }),
        api.get("/consults/summary", { params: { days: 180 } }),
      ]);
      setRows(list.data);
      setSummary(sum.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => {
    api.get("/consults/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/cases", { params: { limit: 500 } }).then((r) => setCases(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = !!meta?.can_edit;
  const set = (k) => (e) => setForm((f) => ({
    ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.case_id) return toast.error("Pick the case first");
    if (!form.consultant_id) return toast.error("Pick the consulting pathologist");
    setBusy("create");
    try {
      await api.post("/consults", form);
      toast.success("Consult requested — the consultant has been notified");
      setForm({ ...EMPTY });
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const saveDetail = async () => {
    setBusy("detail");
    try {
      const { data } = await api.put(`/consults/${detail.id}`, {
        consultant_id: detail.consultant_id, reason: detail.reason, mandated: !!detail.mandated,
        question: detail.question || "", opinion: detail.opinion || "", agreement: detail.agreement,
        diagnosis_changed: !!detail.diagnosis_changed, notes: detail.notes || "",
        answered_at: detail.answered_at || undefined,
      });
      toast.success(data.nonconformance_id
        ? "Saved — a nonconformance was opened in CAPTraX for the major disagreement"
        : "Saved");
      setDetail(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/consults/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PathQity_Consults.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="consults-page">
      <ModuleNav
        title="Intradepartmental Consults"
        blurb="Internal second opinions and policy-mandated double reads — who reviewed the case, how long it took and whether the reviewers agreed."
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="consult-export-button" className="h-11 border-slate-300">
              <Download size={15} className="mr-2" /> Export CSV
            </Button>
            {canEdit && (
              <Button onClick={() => setOpen(true)} data-testid="log-consult-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={16} className="mr-2" /> Request consult
              </Button>
            )}
          </>
        }
      />

      {summary && (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="consult-metrics">
          <Stat label="Consults (180 days)" value={summary.total}
                sub={`${summary.pending} awaiting an opinion · ${summary.mandated} policy-mandated`} />
          <Stat label="Concordance"
                value={summary.concordance_percent === null ? "—" : `${summary.concordance_percent}%`}
                sub={`${summary.minor_disagreements} minor · ${summary.major_disagreements} major`}
                tone={summary.major_disagreements ? "text-red-700" : "text-[#134e4a]"} />
          <Stat label="Median turnaround"
                value={summary.median_hours === null ? "—" : `${summary.median_hours} h`}
                sub={summary.average_hours === null ? "" : `average ${summary.average_hours} h · longest ${summary.longest_hours} h`} />
          <Stat label="Diagnosis changed" value={summary.diagnosis_changed}
                sub={`${summary.deferred_outside} deferred to an outside consult`}
                tone={summary.diagnosis_changed ? "text-amber-700" : "text-[#134e4a]"} />
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="consult-filters">
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 placeholder="Accession, patient, pathologist" data-testid="consult-search" className="h-10 pl-9" />
        </div>
        {[["", "All"], ["pending", "Awaiting opinion"], ["disagreement", "Disagreements"],
          ["major", "Major only"], ["mandated", "Policy-mandated"]].map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilters((f) => ({ ...f, flag: k }))}
                  data-testid={`consult-filter-${k || "all"}`}
                  className={`h-10 border px-3 text-sm font-medium ${filters.flag === k
                    ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
        <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))}
                data-testid="consult-voided-filter" className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Active only</option>
          <option value="true">Voided only</option>
          <option value="all">Everything</option>
        </select>
      </div>

      <section className="border border-slate-200" data-testid="consult-log">
        {rows.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="consult-empty">
            No consults logged for this filter.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1020px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Case</th><th className="px-3 py-2">Requested by</th>
                  <th className="px-3 py-2">Consultant</th><th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Requested</th><th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2">Agreement</th><th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-100 row-hover hover:bg-slate-50 ${r.voided ? "opacity-60" : ""}`}
                      data-testid={`consult-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                      <div className="text-[11px] text-slate-500">{r.patient_name}</div>
                      {r.voided && <div className="text-[11px] font-bold uppercase text-red-800">Voided</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.requested_by_name}</td>
                    <td className="px-3 py-2 text-slate-800">
                      <span className="inline-flex items-center gap-1"><Users size={12} className="text-slate-400" />{r.consultant_name}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {r.reason}
                      {r.mandated && (
                        <div className="mt-0.5 inline-block bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-700">
                          policy review
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{fmtDateTime(r.requested_at)}</td>
                    <td className={`num px-3 py-2 text-right font-bold ${r.pending ? "text-amber-800" : "text-[#134e4a]"}`}>
                      {r.hours !== null ? r.hours : r.hours_open !== null ? `${r.hours_open}…` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${AGREE_STYLE[r.agreement] || "bg-slate-200"}`}>
                        {r.agreement}
                      </span>
                      {r.major && (
                        <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-800">
                          <AlertTriangle size={11} /> CAPA opened
                        </div>
                      )}
                      {r.diagnosis_changed && <div className="text-[11px] text-amber-800">diagnosis changed</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="flex flex-wrap justify-end gap-1.5">
                        {canEdit && !r.voided && (
                          <Button size="sm" variant="outline" onClick={() => setDetail({ ...r })}
                                  data-testid={`consult-edit-${r.id}`} className="h-9 border-slate-300">
                            {r.pending ? "Record opinion" : "Open"}
                          </Button>
                        )}
                        {canEdit && !r.voided && (
                          <Button size="sm" variant="outline" onClick={() => setVoidRow(r)}
                                  data-testid={`consult-void-${r.id}`} className="h-9 border-slate-300">
                            <Ban size={13} />
                          </Button>
                        )}
                        {r.voided && canEdit && (
                          <Button size="sm" variant="outline" data-testid={`consult-reinstate-${r.id}`} className="h-9 border-slate-300"
                                  onClick={async () => {
                                    try { await api.post(`/consults/${r.id}/reinstate`); toast.success("Reinstated"); load(); }
                                    catch (e) { toast.error(apiError(e)); }
                                  }}>
                            <Undo2 size={13} />
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="consult-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Request an intradepartmental consult</DialogTitle>
            <DialogDescription>The consultant is notified and the clock starts now.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2" data-testid="consult-create-form">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Case</Label>
              <select value={form.case_id} onChange={set("case_id")} data-testid="consult-case-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select case…</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.accession_number} — ${c.patient_name}`}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Consulting pathologist</Label>
              <select value={form.consultant_id} onChange={set("consultant_id")} data-testid="consult-pathologist-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select pathologist…</option>
                {(meta?.pathologists || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Reason</Label>
              <select value={form.reason} onChange={set("reason")} data-testid="consult-reason-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {(meta?.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Question for the consultant</Label>
              <Textarea value={form.question} onChange={set("question")} data-testid="consult-question-input"
                        className="mt-1 min-h-[70px] border-slate-300" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input type="checkbox" checked={form.mandated} onChange={set("mandated")}
                     data-testid="consult-mandated" className="h-4 w-4 accent-[#134e4a]" />
              This is a policy-mandated review (e.g. every new malignancy is double read)
            </label>
            <Button type="submit" disabled={busy === "create"} data-testid="consult-create-submit"
                    className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Requesting…" : "Request consult"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="consult-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Consult — <span className="font-mono">{detail?.accession_number}</span>
            </DialogTitle>
            <DialogDescription>
              Record the consultant's opinion and whether the reviewers agreed.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Consultant</Label>
                <select value={detail.consultant_id} data-testid="consult-detail-consultant"
                        onChange={(e) => setDetail((d) => ({ ...d, consultant_id: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.pathologists || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Reason</Label>
                <select value={detail.reason} data-testid="consult-detail-reason"
                        onChange={(e) => setDetail((d) => ({ ...d, reason: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Question asked</Label>
                <Textarea value={detail.question || ""} data-testid="consult-detail-question"
                          onChange={(e) => setDetail((d) => ({ ...d, question: e.target.value }))}
                          className="mt-1 min-h-[60px] border-slate-300" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Consultant's opinion</Label>
                <Textarea value={detail.opinion || ""} data-testid="consult-detail-opinion"
                          onChange={(e) => setDetail((d) => ({ ...d, opinion: e.target.value }))}
                          className="mt-1 min-h-[90px] border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Agreement</Label>
                <select value={detail.agreement} data-testid="consult-detail-agreement"
                        onChange={(e) => setDetail((d) => ({ ...d, agreement: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.agreements || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Answered at</Label>
                <Input type="datetime-local" data-testid="consult-detail-answered"
                       value={(detail.answered_at || "").slice(0, 16)}
                       onChange={(e) => setDetail((d) => ({ ...d, answered_at: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                <input type="checkbox" checked={!!detail.diagnosis_changed} data-testid="consult-detail-changed"
                       onChange={(e) => setDetail((d) => ({ ...d, diagnosis_changed: e.target.checked }))}
                       className="h-4 w-4 accent-[#134e4a]" />
                The final diagnosis changed as a result of this review
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                <input type="checkbox" checked={!!detail.mandated} data-testid="consult-detail-mandated"
                       onChange={(e) => setDetail((d) => ({ ...d, mandated: e.target.checked }))}
                       className="h-4 w-4 accent-[#134e4a]" />
                Policy-mandated review
              </label>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                <Textarea value={detail.notes || ""} data-testid="consult-detail-notes"
                          onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))}
                          className="mt-1 min-h-[60px] border-slate-300" />
              </div>
              {detail.agreement === "Major disagreement" && (
                <p className="sm:col-span-2 text-xs font-semibold text-red-800">
                  Saving a major disagreement opens a nonconformance in CAPTraX automatically.
                </p>
              )}
              <Button onClick={saveDetail} disabled={busy === "detail"} data-testid="consult-detail-save"
                      className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
                {busy === "detail" ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <RetireDialog
        open={!!voidRow} onOpenChange={(o) => !o && setVoidRow(null)}
        title="Void consult record"
        description="The record stays in the log with the reason attached for the inspector."
        confirmLabel="Void record"
        testid="consult-void-dialog"
        onConfirm={async (reason) => {
          await api.post(`/consults/${voidRow.id}/void`, { reason });
          toast.success("Voided");
          setVoidRow(null);
          load();
        }}
      />
    </div>
  );
}
